import React, { Fragment, useContext, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";

import { EligibilityContext } from "../../contexts/EligibilityContext";

import CriteriaRadio from "./CriteriaRadio";
import CriteriaPicklist from "./CriteriaPicklist";
import CriteriaText from "./CriteriaText";
import CriteriaCheckboxes from "./CriteriaCheckboxes";

const apiUrl = "/wp-json/salesforce/v1/funds/eligibility";

// Function for sorting criteria by groupNo and sequence
const sortCriteria = (criteria) => {
  return criteria.sort(function (a, b) {
    // Parse the string values as integers for comparison
    const groupNoA = parseInt(a.groupNo, 10);
    const groupNoB = parseInt(b.groupNo, 10);

    // Compare the integer values in ascending order
    if (groupNoA < groupNoB) return -1;
    if (groupNoA > groupNoB) return 1;

    // If groupNo is equal, then compare sequence as integers in ascending order
    const sequenceA = parseInt(a.sequence, 10);
    const sequenceB = parseInt(b.sequence, 10);

    if (sequenceA < sequenceB) return -1;
    if (sequenceA > sequenceB) return 1;

    return 0; // If both properties are equal
  });
};

const Eligibility = () => {
  const context = useContext(EligibilityContext);
  const [checking, setChecking] = useState(false);
  const { register, handleSubmit, errors } = useForm();

  const handleSubmission = (formData) => {
    setChecking(true);

    const criteria = Object.keys(formData).map((key) => {
      return JSON.parse(formData[key]);
    });

    const requestBody = {
      Criterias: criteria,
      Medication: context.state.medications
        .map((medication) => medication.Id)
        .join(";"),
      CurrentScreenStep: 2,
    };

    axios
      .post(apiUrl, requestBody)
      .then((response) => {
        context.actions.setResponse(response.data);

        if (response.data.eligibility === true) {
          context.actions.setStep(4);
        } else {
          reportDenialReason(response);
          const supportProgramsUrl = `/wp-json/salesforce/v1/funds/${context.state.diseaseFund.fundId}/supportprograms`;
          axios
            .get(supportProgramsUrl)
            .then((response) => {
              context.actions.setSupportPrograms(response.data.Partners || []);
              context.actions.setStep(5);
            })
            .catch(() => {
              context.actions.renderError();
            });
        }
      })
      .catch(() => {
        context.actions.renderError();
      });
  };

  const reportDenialReason = (response) => {
    if (window.dataLayer && response.data.eligibility === false) {
      window.dataLayer.push({
        event: "Eligibility",
        reason: response.data.message,
      });
    }
  };

  return (
    <Fragment>
      <p className="f-h3">
        Eligibility Pre-screen for {context.state.diseaseFund.name}
      </p>
      <p className="f-h5">
        Selected medication(s) or service{" "}
        <button
          onClick={() => context.actions.setStep(2)}
          className="btn-transparentSmall"
          style={{ marginLeft: 20 }}
        >
          Edit
        </button>
      </p>
      <div className="u-richtext f-scale-body u-mb3">
        <ul>
          {context.state.medications.map((medication, i) => (
            <li key={i} className="u-mb1">
              {medication.Brand_Name} ({medication.Generic_Name})
            </li>
          ))}
        </ul>
      </div>
      <form onSubmit={handleSubmit(handleSubmission)} className="u-form">
        {sortCriteria(context.state.diseaseFund.eligibilityCriteria).map(
          (criteria, i) => {
            switch (criteria.answerType) {
              case "Radio":
                return (
                  <CriteriaRadio
                    key={i}
                    criteria={criteria}
                    register={register}
                    errors={errors}
                  />
                );
              case "Picklist":
                return (
                  <CriteriaPicklist
                    key={i}
                    criteria={criteria}
                    register={register}
                    errors={errors}
                  />
                );
              case "Text":
                return (
                  <CriteriaText
                    key={i}
                    criteria={criteria}
                    register={register}
                    errors={errors}
                  />
                );
              case "Checkbox":
                return (
                  <CriteriaCheckboxes
                    key={i}
                    criteria={criteria}
                    register={register}
                    errors={errors}
                  />
                );
            }
          }
        )}

        <button type="submit" className="btn" disabled={checking}>
          {checking && <Fragment>Checking Eligibility...</Fragment>}
          {!checking && <Fragment>Next</Fragment>}
        </button>
      </form>

      <p className="u-mt4">
        <b>Please note:</b> To qualify for assistance, patients must be
        receiving treatment for the selected disease or qualify for the fund.
      </p>
    </Fragment>
  );
};

export default Eligibility;
